import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Stack,
  Container,
  Switch,
  FormControlLabel,
  IconButton,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import DashboardLayout from '../../layouts/DashboardLayout';
import apiClient from '../../services/api';

const rupees = (paise) => (Number(paise) || 0) / 100;

const emptyCouponForm = () => ({
  code: '',
  description: '',
  discount_type: 'percent',
  discount_value: 10,
  max_redemptions: '',
  min_subtotal_paise: 0,
  valid_from: '',
  valid_until: '',
  is_active: true,
});

const FranchiseeSaasPricingAdmin = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [features, setFeatures] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [savingKey, setSavingKey] = useState('');
  const [couponDialog, setCouponDialog] = useState(false);
  const [editingCouponId, setEditingCouponId] = useState(null);
  const [couponForm, setCouponForm] = useState(emptyCouponForm());
  const [couponSaving, setCouponSaving] = useState(false);
  const [couponDeletingId, setCouponDeletingId] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const pricingRes = await apiClient.get('/franchisee/admin/saas-pricing');
      if (pricingRes.data?.success) {
        setFeatures(pricingRes.data.data?.features || []);
        setCoupons(pricingRes.data.data?.coupons || []);
      } else {
        setError(pricingRes.data?.message || 'Failed to load');
      }
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load pricing');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const saveFeature = async (row) => {
    setSavingKey(row.feature_key);
    setError('');
    try {
      await apiClient.put(`/franchisee/admin/saas-pricing/features/${row.feature_key}`, {
        display_label: row.display_label,
        amount_paise: Math.round(Number(row.amount_paise) || 0),
        tenure_days: Math.round(Number(row.tenure_days) || 30),
        is_active: Boolean(row.is_active),
        sort_order: Math.round(Number(row.sort_order) || 0),
      });
      await load();
    } catch (e) {
      setError(e.response?.data?.message || 'Save failed');
    } finally {
      setSavingKey('');
    }
  };

  const openCreateCoupon = () => {
    setEditingCouponId(null);
    setCouponForm(emptyCouponForm());
    setCouponDialog(true);
  };

  const openEditCoupon = (coupon) => {
    setEditingCouponId(coupon.id);
    setCouponForm({
      code: coupon.code || '',
      description: coupon.description || '',
      discount_type: coupon.discount_type || 'percent',
      discount_value:
        coupon.discount_type === 'fixed_paise'
          ? rupees(coupon.discount_value)
          : coupon.discount_value ?? 10,
      max_redemptions:
        coupon.max_redemptions != null ? String(coupon.max_redemptions) : '',
      min_subtotal_paise: Number(coupon.min_subtotal_paise) || 0,
      valid_from: coupon.valid_from ? String(coupon.valid_from).slice(0, 10) : '',
      valid_until: coupon.valid_until ? String(coupon.valid_until).slice(0, 10) : '',
      is_active: Boolean(coupon.is_active),
    });
    setCouponDialog(true);
  };

  const buildCouponPayload = () => ({
    code: couponForm.code.trim().toUpperCase(),
    description: couponForm.description.trim() || null,
    discount_type: couponForm.discount_type,
    discount_value:
      couponForm.discount_type === 'fixed_paise'
        ? Math.round(Number(couponForm.discount_value) * 100)
        : Number(couponForm.discount_value),
    max_redemptions: couponForm.max_redemptions
      ? Math.floor(Number(couponForm.max_redemptions))
      : null,
    min_subtotal_paise: Math.floor(Number(couponForm.min_subtotal_paise) || 0),
    valid_from: couponForm.valid_from || null,
    valid_until: couponForm.valid_until || null,
    is_active: Boolean(couponForm.is_active),
  });

  const saveCoupon = async () => {
    if (!couponForm.code.trim()) {
      setError('Coupon code is required');
      return;
    }
    setCouponSaving(true);
    setError('');
    try {
      const payload = buildCouponPayload();
      if (editingCouponId) {
        await apiClient.patch(
          `/franchisee/admin/saas-pricing/coupons/${editingCouponId}`,
          payload
        );
      } else {
        await apiClient.post('/franchisee/admin/saas-pricing/coupons', payload);
      }
      setCouponDialog(false);
      setEditingCouponId(null);
      setCouponForm(emptyCouponForm());
      await load();
    } catch (e) {
      setError(e.response?.data?.message || 'Could not save coupon');
    } finally {
      setCouponSaving(false);
    }
  };

  const toggleCouponActive = async (coupon, nextActive) => {
    setError('');
    try {
      await apiClient.patch(`/franchisee/admin/saas-pricing/coupons/${coupon.id}`, {
        is_active: nextActive,
      });
      setCoupons((prev) =>
        prev.map((c) => (c.id === coupon.id ? { ...c, is_active: nextActive } : c))
      );
    } catch (e) {
      setError(e.response?.data?.message || 'Could not update coupon status');
    }
  };

  const deleteCoupon = async (coupon) => {
    const sure = window.confirm(
      `Delete coupon "${coupon.code}"? This cannot be undone.`
    );
    if (!sure) return;
    setCouponDeletingId(coupon.id);
    setError('');
    try {
      await apiClient.delete(`/franchisee/admin/saas-pricing/coupons/${coupon.id}`);
      await load();
    } catch (e) {
      setError(e.response?.data?.message || 'Could not delete coupon');
    } finally {
      setCouponDeletingId('');
    }
  };

  return (
    <DashboardLayout>
      <Container maxWidth="lg">
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h5" fontWeight={700}>
            Franchisee SaaS pricing
          </Typography>
          <Button component={RouterLink} to="/locsy/admin/verification" variant="outlined">
            Back to verification
          </Button>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Set list prices and tenure (days) per dashboard feature. New Mitra accounts activate automatically
          after registration (Easy Start = pay later / Launch Boost = pay now) — no admin approval step.
        </Typography>

        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Registration & verification
          </Typography>
          <Typography variant="body2" color="text.secondary">
            After registration, businesses are published to Citizen My Town with a Not verified badge and
            Mitra dashboard access starts immediately. App admins verify profiles from Business Admin
            Verification — approval is no longer required.
          </Typography>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Paper sx={{ p: 2, mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Feature prices
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Feature</TableCell>
                    <TableCell>Label</TableCell>
                    <TableCell align="right">Amount (paise)</TableCell>
                    <TableCell align="right">Tenure (days)</TableCell>
                    <TableCell align="right">Sort</TableCell>
                    <TableCell>Active</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {features.map((row) => (
                    <TableRow key={row.feature_key}>
                      <TableCell>{row.feature_key}</TableCell>
                      <TableCell sx={{ minWidth: 160 }}>
                        <TextField
                          size="small"
                          fullWidth
                          value={row.display_label}
                          onChange={(e) =>
                            setFeatures((prev) =>
                              prev.map((r) =>
                                r.feature_key === row.feature_key
                                  ? { ...r, display_label: e.target.value }
                                  : r
                              )
                            )
                          }
                        />
                      </TableCell>
                      <TableCell align="right" sx={{ maxWidth: 140 }}>
                        <TextField
                          size="small"
                          type="number"
                          inputProps={{ min: 0, step: 0.01 }}
                          label="₹"
                          value={rupees(row.amount_paise)}
                          onChange={(e) => {
                            const rupeeVal = e.target.value;
                            const paise =
                              rupeeVal === '' || rupeeVal == null
                                ? 0
                                : Math.round(Number(rupeeVal) * 100);
                            setFeatures((prev) =>
                              prev.map((r) =>
                                r.feature_key === row.feature_key
                                  ? { ...r, amount_paise: paise }
                                  : r
                              )
                            );
                          }}
                        />
                        <Typography variant="caption" display="block" color="text.secondary">
                          {Math.round(Number(row.amount_paise) || 0)} paise
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <TextField
                          size="small"
                          type="number"
                          value={row.tenure_days}
                          onChange={(e) =>
                            setFeatures((prev) =>
                              prev.map((r) =>
                                r.feature_key === row.feature_key
                                  ? { ...r, tenure_days: e.target.value }
                                  : r
                              )
                            )
                          }
                        />
                      </TableCell>
                      <TableCell align="right">
                        <TextField
                          size="small"
                          type="number"
                          value={row.sort_order}
                          onChange={(e) =>
                            setFeatures((prev) =>
                              prev.map((r) =>
                                r.feature_key === row.feature_key
                                  ? { ...r, sort_order: e.target.value }
                                  : r
                              )
                            )
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          select
                          value={row.is_active ? 'yes' : 'no'}
                          onChange={(e) =>
                            setFeatures((prev) =>
                              prev.map((r) =>
                                r.feature_key === row.feature_key
                                  ? { ...r, is_active: e.target.value === 'yes' }
                                  : r
                              )
                            )
                          }
                        >
                          <MenuItem value="yes">Yes</MenuItem>
                          <MenuItem value="no">No</MenuItem>
                        </TextField>
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          variant="contained"
                          disabled={savingKey === row.feature_key}
                          onClick={() => saveFeature(row)}
                        >
                          {savingKey === row.feature_key ? <CircularProgress size={18} /> : 'Save'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>

            <Paper sx={{ p: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h6">Registration coupons</Typography>
                <Button variant="outlined" onClick={openCreateCoupon}>
                  Add coupon
                </Button>
              </Stack>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Code</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell align="right">Discount</TableCell>
                    <TableCell align="right">Min order</TableCell>
                    <TableCell align="right">Uses</TableCell>
                    <TableCell>Active</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {coupons.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        No coupons yet
                      </TableCell>
                    </TableRow>
                  ) : (
                    coupons.map((c) => (
                      <TableRow key={c.id}>
                        <TableCell>
                          <Typography fontWeight={600}>{c.code}</Typography>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 160 }}>
                          {c.description || '—'}
                        </TableCell>
                        <TableCell>{c.discount_type}</TableCell>
                        <TableCell align="right">
                          {c.discount_type === 'percent'
                            ? `${c.discount_value}%`
                            : `₹${rupees(c.discount_value).toFixed(2)}`}
                        </TableCell>
                        <TableCell align="right">
                          ₹{rupees(c.min_subtotal_paise).toFixed(2)}
                        </TableCell>
                        <TableCell align="right">
                          {c.redemption_count}
                          {c.max_redemptions != null ? ` / ${c.max_redemptions}` : ''}
                        </TableCell>
                        <TableCell>
                          <Switch
                            size="small"
                            checked={Boolean(c.is_active)}
                            onChange={(e) => toggleCouponActive(c, e.target.checked)}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            aria-label="Edit coupon"
                            onClick={() => openEditCoupon(c)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            aria-label="Delete coupon"
                            disabled={couponDeletingId === c.id}
                            onClick={() => deleteCoupon(c)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Paper>

            <Dialog
              open={couponDialog}
              onClose={() => !couponSaving && setCouponDialog(false)}
              maxWidth="xs"
              fullWidth
            >
              <DialogTitle>{editingCouponId ? 'Edit coupon' : 'New coupon'}</DialogTitle>
              <DialogContent>
                <TextField
                  fullWidth
                  margin="dense"
                  label="Code"
                  value={couponForm.code}
                  onChange={(e) =>
                    setCouponForm({ ...couponForm, code: e.target.value.toUpperCase() })
                  }
                />
                <TextField
                  fullWidth
                  margin="dense"
                  label="Description"
                  value={couponForm.description}
                  onChange={(e) =>
                    setCouponForm({ ...couponForm, description: e.target.value })
                  }
                />
                <TextField
                  fullWidth
                  margin="dense"
                  select
                  label="Discount type"
                  value={couponForm.discount_type}
                  onChange={(e) =>
                    setCouponForm({ ...couponForm, discount_type: e.target.value })
                  }
                >
                  <MenuItem value="percent">Percent (%)</MenuItem>
                  <MenuItem value="fixed_paise">Fixed amount (₹)</MenuItem>
                </TextField>
                <TextField
                  fullWidth
                  margin="dense"
                  type="number"
                  label={
                    couponForm.discount_type === 'percent'
                      ? 'Discount (%)'
                      : 'Discount (₹)'
                  }
                  value={couponForm.discount_value}
                  onChange={(e) =>
                    setCouponForm({ ...couponForm, discount_value: e.target.value })
                  }
                  helperText={
                    couponForm.discount_type === 'fixed_paise'
                      ? 'Enter amount in rupees'
                      : 'Enter percentage 0–100'
                  }
                />
                <TextField
                  fullWidth
                  margin="dense"
                  type="number"
                  label="Max redemptions (empty = unlimited)"
                  value={couponForm.max_redemptions}
                  onChange={(e) =>
                    setCouponForm({ ...couponForm, max_redemptions: e.target.value })
                  }
                />
                <TextField
                  fullWidth
                  margin="dense"
                  type="number"
                  inputProps={{ min: 0, step: 0.01 }}
                  label="Min subtotal (₹)"
                  value={rupees(couponForm.min_subtotal_paise)}
                  onChange={(e) => {
                    const rupeeVal = e.target.value;
                    const paise =
                      rupeeVal === '' ? 0 : Math.round(Number(rupeeVal) * 100);
                    setCouponForm({ ...couponForm, min_subtotal_paise: paise });
                  }}
                />
                <TextField
                  fullWidth
                  margin="dense"
                  type="date"
                  label="Valid from"
                  InputLabelProps={{ shrink: true }}
                  value={couponForm.valid_from}
                  onChange={(e) =>
                    setCouponForm({ ...couponForm, valid_from: e.target.value })
                  }
                />
                <TextField
                  fullWidth
                  margin="dense"
                  type="date"
                  label="Valid until"
                  InputLabelProps={{ shrink: true }}
                  value={couponForm.valid_until}
                  onChange={(e) =>
                    setCouponForm({ ...couponForm, valid_until: e.target.value })
                  }
                />
                <FormControlLabel
                  sx={{ mt: 1 }}
                  control={
                    <Switch
                      checked={couponForm.is_active}
                      onChange={(e) =>
                        setCouponForm({ ...couponForm, is_active: e.target.checked })
                      }
                    />
                  }
                  label="Active"
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setCouponDialog(false)} disabled={couponSaving}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={saveCoupon}
                  disabled={!couponForm.code.trim() || couponSaving}
                >
                  {couponSaving ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : editingCouponId ? (
                    'Save'
                  ) : (
                    'Create'
                  )}
                </Button>
              </DialogActions>
            </Dialog>
          </>
        )}
      </Container>
    </DashboardLayout>
  );
};

export default FranchiseeSaasPricingAdmin;
